import { useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Lock, Plus, Share2, TicketPercent, X } from "lucide-react";
import api from "../lib/api";
import { imgProps } from "../lib/img";
import { useCart } from "../context/CartContext";
import { usePromoPreview } from "../lib/usePromos";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { Input } from "./ui/input";

const money = (n) => `$${(n || 0).toFixed(2)}`;

export const shareOffer = async (code, headline) => {
  const url = `${window.location.origin}/offer/${code}`;
  if (navigator.share) {
    try { await navigator.share({ title: headline || `Urban offer ${code}`, url }); return; } catch { /* fall through */ }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Offer link copied — share it with friends!");
  } catch { toast(url); }
};

function OfferCard({ offer, applied, onApply, onRemove }) {
  const locked = offer.status === "locked";
  const unavailable = offer.status === "unavailable";
  const progress = locked && offer.min_order
    ? Math.min(100, ((offer.min_order - offer.remaining) / offer.min_order) * 100) : 0;
  return (
    <div data-testid={`offer-sheet-card-${offer.id}`}
      className={`rounded-2xl border p-4 ${applied ? "border-green-500 bg-green-50" : unavailable ? "border-neutral-200 bg-neutral-50 opacity-60" : "border-dashed border-[#E04F33]/50 bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display font-extrabold text-sm leading-tight">{offer.headline || offer.name}</p>
          {offer.code && <p className="text-xs font-bold text-[#E04F33] mt-1 tracking-wide">{offer.code}</p>}
          {!offer.code && <p className="text-[11px] font-bold text-green-600 mt-1">Auto-applied at checkout</p>}
          <p className="text-[11px] text-neutral-500 mt-1">{offer.fine_print}</p>
          {offer.status === "eligible" && offer.savings > 0 && (
            <p className="text-xs font-bold text-green-600 mt-1.5" data-testid={`offer-savings-${offer.id}`}>
              Save {money(offer.savings)} on this order
              {offer.max_discount && offer.savings >= offer.max_discount ? ` · maximum ${money(offer.max_discount)} discount` : ""}
            </p>
          )}
          {locked && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-amber-700" data-testid={`offer-remaining-${offer.id}`}>
                Add {money(offer.remaining)} more in eligible food to unlock
              </p>
              <div className="h-1.5 bg-neutral-100 rounded-full mt-1.5 overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
          {unavailable && <p className="text-xs text-neutral-500 mt-1.5">{offer.reason}</p>}
          {offer.code && (
            <button data-testid={`offer-share-${offer.code}`} onClick={() => shareOffer(offer.code, offer.headline)}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-neutral-400 hover:text-[#E04F33] mt-2 transition-colors">
              <Share2 size={11} aria-hidden="true" /> Share this offer
            </button>
          )}
        </div>
        <div className="shrink-0">
          {applied ? (
            <button data-testid="offer-remove-applied" onClick={onRemove}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-600 text-white text-xs font-bold">
              <BadgeCheck size={13} /> Applied <X size={12} />
            </button>
          ) : offer.status === "eligible" && offer.code ? (
            <button data-testid={`offer-apply-${offer.code}`} onClick={() => onApply(offer)}
              className="px-4 py-1.5 rounded-full bg-[#E04F33] text-white text-xs font-bold hover:bg-[#C94228]">
              Apply
            </button>
          ) : locked ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-neutral-400"><Lock size={11} /> Locked</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function OffersSheet({ open, onOpenChange }) {
  const { foodCart, channel, coupon, setCoupon, addFood } = useCart();
  const preview = usePromoPreview(foodCart, channel, open);
  const [codeInput, setCodeInput] = useState("");
  const [checking, setChecking] = useState(false);

  const offers = preview?.offers || [];
  const appliedOffer = coupon ? offers.find((o) => o.code === coupon) : null;
  const best = preview?.best;
  const betterThanApplied = best && best.code && appliedOffer?.status === "eligible"
    && best.code !== coupon && best.savings > (appliedOffer.savings || 0);

  const applyCode = async (raw) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code) return;
    setChecking(true);
    try {
      const { data } = await api.post("/promos/validate", {
        code, channel,
        items: foodCart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      });
      setCoupon(data.code);
      setCodeInput("");
      toast.success(`${data.code} applied — you'll save ${money(data.savings)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "We couldn't check that coupon right now. Please try again.");
    }
    setChecking(false);
  };

  const removeCoupon = () => { setCoupon(""); toast("Coupon removed"); };

  const eligible = offers.filter((o) => o.status === "eligible" && o.code !== coupon);
  const locked = offers.filter((o) => o.status === "locked");
  const unavailable = offers.filter((o) => o.status === "unavailable" && (o.members_only || o.first_order_only));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl sm:max-w-lg sm:mx-auto pb-8" data-testid="offers-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-xl flex items-center gap-2">
            <TicketPercent size={20} className="text-[#E04F33]" /> Offers & Coupons
          </SheetTitle>
          <SheetDescription className="sr-only">Browse available offers, enter a coupon code or apply the best deal to your cart.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          <div>
            <p className="text-xs font-bold text-neutral-500 mb-2">Have a coupon code?</p>
            <div className="flex gap-2">
              <Input data-testid="coupon-code-input" placeholder="Enter code e.g. URBAN10"
                value={codeInput} onChange={(e) => setCodeInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !checking && applyCode(codeInput)}
                className="rounded-xl uppercase" />
              <button data-testid="coupon-apply-button" disabled={checking || !codeInput.trim()}
                onClick={() => applyCode(codeInput)}
                className="px-5 rounded-xl bg-[#0F0F0F] text-white text-sm font-bold disabled:opacity-40">
                {checking ? "…" : "Apply"}
              </button>
            </div>
          </div>

          {appliedOffer && appliedOffer.status === "eligible" && (
            <div>
              <p className="text-xs font-bold text-neutral-500 mb-2">Applied</p>
              <OfferCard offer={appliedOffer} applied onRemove={removeCoupon} />
              {betterThanApplied && (
                <button data-testid="switch-better-offer" onClick={() => applyCode(best.code)}
                  className="mt-2 w-full text-left text-xs font-bold text-[#E04F33] bg-[#E04F33]/5 border border-[#E04F33]/20 rounded-xl px-4 py-3">
                  Switch to {best.code} and save {money(best.savings - appliedOffer.savings)} more →
                </button>
              )}
            </div>
          )}
          {coupon && appliedOffer && appliedOffer.status !== "eligible" && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3" data-testid="coupon-invalid-warning">
              <p className="text-xs font-bold text-amber-800">{coupon} no longer applies — {appliedOffer.reason}</p>
              <button data-testid="coupon-invalid-remove" onClick={removeCoupon} className="text-xs underline text-amber-700 mt-1">Remove coupon</button>
            </div>
          )}

          {best && !appliedOffer && (
            <div>
              <p className="text-xs font-bold text-neutral-500 mb-2">Best offer for your cart</p>
              <OfferCard offer={best} onApply={(o) => applyCode(o.code)} />
            </div>
          )}

          {eligible.filter((o) => o.id !== best?.id).length > 0 && (
            <div>
              <p className="text-xs font-bold text-neutral-500 mb-2">Available offers</p>
              <div className="space-y-2.5">
                {eligible.filter((o) => o.id !== best?.id).map((o) => (
                  <OfferCard key={o.id} offer={o} onApply={(x) => applyCode(x.code)} />
                ))}
              </div>
            </div>
          )}

          {locked.length > 0 && (
            <div>
              <p className="text-xs font-bold text-neutral-500 mb-2">Add more to unlock</p>
              <div className="space-y-2.5">
                {locked.map((o) => <OfferCard key={o.id} offer={o} />)}
              </div>
            </div>
          )}

          {preview?.unlock?.suggestions?.length > 0 && (
            <div data-testid="unlock-suggestions">
              <p className="text-xs font-bold text-neutral-500 mb-2">
                Add {money(preview.unlock.remaining)} more — {preview.unlock.label}
              </p>
              <div className="flex gap-2.5 overflow-x-auto pb-1">
                {preview.unlock.suggestions.map((p) => (
                  <div key={p.id} data-testid={`unlock-suggestion-${p.id}`}
                    className="shrink-0 w-36 border rounded-xl p-2.5 bg-white">
                    <img {...imgProps(p.image, [320], "160px")} alt={p.name} width="160" height="64" className="w-full h-16 rounded-lg object-cover" loading="lazy" />
                    <p className="text-[11px] font-semibold mt-1.5 leading-tight line-clamp-2">{p.name}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs font-bold">{money(p.display_price)}</span>
                      <button data-testid={`unlock-add-${p.id}`}
                        onClick={() => { addFood(p); toast.success(`${p.name} added`); }}
                        className="inline-flex items-center gap-0.5 px-2.5 py-1 rounded-full bg-[#E04F33] text-white text-[11px] font-bold">
                        <Plus size={11} aria-hidden="true" /> Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {unavailable.length > 0 && (
            <div>
              <p className="text-xs font-bold text-neutral-500 mb-2">Not available for you</p>
              <div className="space-y-2.5">
                {unavailable.map((o) => <OfferCard key={o.id} offer={o} />)}
              </div>
            </div>
          )}

          {!offers.length && (
            <p className="text-sm text-neutral-400 text-center py-6" data-testid="offers-empty">
              No offers are available for this cart right now.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
